// src/components/promos/SimulatorModal.tsx
import { useState } from "react";
import Modal from "@/components/shared/ui/Modal";
import Input from "@/components/shared/ui/Input";
import { useSimulatePromoMutation } from "@/api/features/promos";
import type { Promo, SimulateResult } from "@/api/features/promos";

export default function SimulatorModal({ promo, onClose }: { promo: Promo; onClose: () => void }) {
  const [productType, setProductType] = useState<"flight" | "tour" | "hotel">("flight");
  const [currency, setCurrency] = useState(promo.currencyCode ?? "INR");
  const [subtotal, setSubtotal] = useState("10000");
  const [grandTotal, setGrandTotal] = useState("11800");
  const [result, setResult] = useState<SimulateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sim = useSimulatePromoMutation(promo.id);

  const run = () => {
    setError(null);
    setResult(null);
    sim.mutate(
      {
        productType,
        currency: currency.trim().toUpperCase(),
        amounts: { subtotal: Number(subtotal) || 0, grandTotal: Number(grandTotal) || 0 },
      },
      {
        onSuccess: (r) => setResult(r),
        onError: (e) => setError(e.message || "Simulation failed"),
      }
    );
  };

  return (
    <Modal open title={`Simulate — ${promo.name}`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          Preview the discount this promo produces on a sample amount. Math only — skips
          user/limit/window checks.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">Product</span>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value as any)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="flight">Flight</option>
              <option value="tour">Tour</option>
              <option value="hotel">Hotel</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">Currency</span>
            <Input value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">Subtotal</span>
            <Input type="number" value={subtotal} onChange={(e) => setSubtotal(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">Grand total</span>
            <Input type="number" value={grandTotal} onChange={(e) => setGrandTotal(e.target.value)} />
          </label>
        </div>

        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
        ) : null}

        {result ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Discount</span><span className="font-semibold text-emerald-800">-{result.discountAmount.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">New total</span><span className="font-semibold text-gray-900">{result.discountedTotal.toLocaleString()}</span></div>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
          <button onClick={run} disabled={sim.isPending} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60">
            {sim.isPending ? "Computing…" : "Simulate"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
